import React, { Suspense, useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import logo from './logo.svg';
import Navbar from './components/Navbar';
import UserHeader from './components/UserHeaderNew';
import { ErrorBoundary, SectionErrorBoundary, LoadingFallback } from './components/ErrorBoundary';
import { SessionTimeoutWarning } from './components/SessionTimeout';
import { ForcePasswordUpgrade } from './components/ForcePasswordUpgrade';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CurrencyDisplayProvider } from './contexts/CurrencyDisplayContext';
import { DashboardsProvider } from './contexts/DashboardsContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { ImportJobProvider } from './components/import/shared/ImportJobContext';
import { TopBarSlotProvider } from './contexts/TopBarSlotContext';
import ProtectedRoute from './components/ProtectedRoute';
import { SECTION_ROUTES, SUB_ROUTES, DEFAULT_PATH, LOGIN_PATH, FORGOT_PASSWORD_PATH, RESET_PASSWORD_PATH, ACCOUNT_PATH } from './config/routes';
import { lazy } from 'react';
import './App.css';

const InviteAccept = lazy(() => import('./components/InviteAccept'));
const ActivateAccount = lazy(() => import('./components/ActivateAccount'));
const LoginPage = lazy(() => import('./components/LoginPage'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const AccountSettings = lazy(() => import('./components/AccountSettings'));
const NavMockup = lazy(() => import('./components/NavMockup'));
const UpgradePage = lazy(() => import('./components/UpgradePage'));
const UpgradeSectionPage = lazy(() => import('./components/UpgradeSectionPage'));

/**
 * Wrapper for InviteAccept that extracts the token from route params
 * and navigates home on completion.
 */
function InviteAcceptWrapper(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  return (
    <InviteAccept
      token={token!}
      onComplete={() => navigate(DEFAULT_PATH, { replace: true })}
    />
  );
}

/**
 * Derive a human-readable section name from the current URL path
 * for use in error boundaries and loading messages.
 */
function useSectionName(): string {
  const { pathname } = useLocation();
  const route = SECTION_ROUTES.find(r => r.path === pathname);
  if (route) return route.label;
  // Handle sub-routes (e.g. /frameworks/123 -> Frameworks)
  const parentRoute = SECTION_ROUTES.find(r => pathname.startsWith(r.path + '/'));
  return parentRoute?.label ?? 'this section';
}

/**
 * Back-compat redirect for the old ``/reporting/v2`` URLs (v2 reporting now
 * lives at ``/reporting``). Rewrites the path prefix and preserves the
 * dashboard id and any query string, so old bookmarks / shared dashboard
 * links keep working: ``/reporting/v2/dashboard/7`` -> ``/reporting/dashboard/7``,
 * ``/reporting/v2?widget=5`` -> ``/reporting?widget=5``.
 */
function ReportingV2Redirect(): React.ReactElement {
  const location = useLocation();
  const target =
    location.pathname.replace(/^\/reporting\/v2/, '/reporting') + location.search;
  return <Navigate to={target} replace />;
}

/**
 * Main application content component.
 * Separated to use hooks within AuthProvider/BrowserRouter context.
 */
function AppContent(): React.ReactElement {
  const sectionName = useSectionName();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated, isLoading, selectedOrganisationId } = useAuth();
  const location = useLocation();

  // Track the previous org ID so we only remount the content tree when the
  // user deliberately switches organisations — NOT on every storage/auth
  // event, which was destroying all local component state (product
  // corrections, form data, report filters, etc.).
  const prevOrgIdRef = useRef(selectedOrganisationId);
  const [orgKey, setOrgKey] = useState(selectedOrganisationId || 'none');

  useEffect(() => {
    if (selectedOrganisationId === null) {
      return;
    }
    if (prevOrgIdRef.current === null) {
      // First time the org id is known after auth loads. Record it so
      // a real switch later still triggers a remount — but DON'T bump
      // orgKey now: doing so would remount the Routes subtree on every
      // hard refresh and reset within-page state (active tab, wizard
      // progress, scroll position).
      prevOrgIdRef.current = selectedOrganisationId;
      return;
    }
    if (selectedOrganisationId !== prevOrgIdRef.current) {
      prevOrgIdRef.current = selectedOrganisationId;
      setOrgKey(selectedOrganisationId);
    }
  }, [selectedOrganisationId]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Public routes that don't need the app shell (sidebar/header)
  const isLoginRoute = location.pathname === LOGIN_PATH;
  const isForgotPasswordRoute = location.pathname === FORGOT_PASSWORD_PATH;
  const isResetPasswordRoute = location.pathname === RESET_PASSWORD_PATH;
  const isEsgRoute = location.pathname === '/esg';
  const isInviteRoute = location.pathname.startsWith('/invite/');
  const isActivateRoute = location.pathname === '/activate';
  const isNavMockupRoute = location.pathname === '/nav-mockup';
  const isPublicAuthRoute = isLoginRoute || isForgotPasswordRoute || isResetPasswordRoute;

  if (isNavMockupRoute) {
    return (
      <Suspense fallback={<LoadingFallback message="Loading..." />}>
        <NavMockup />
      </Suspense>
    );
  }

  // Show login page without app shell
  if (isLoginRoute) {
    if (isAuthenticated) {
      // Honour the path that initiated the bounce-to-login (preserved
      // by ProtectedRoute as location.state.from) so a hard refresh on
      // /facilities lands back on /facilities, not the dashboard.
      const from = (location.state as { from?: string } | null)?.from;
      const target = from && from !== LOGIN_PATH ? from : DEFAULT_PATH;
      return <Navigate to={target} replace />;
    }
    return (
      <Suspense fallback={<LoadingFallback message="Loading..." />}>
        <LoginPage />
      </Suspense>
    );
  }

  // Show forgot password page without app shell
  if (isForgotPasswordRoute) {
    return (
      <Suspense fallback={<LoadingFallback message="Loading..." />}>
        <ForgotPassword />
      </Suspense>
    );
  }

  // Show reset password page without app shell
  if (isResetPasswordRoute) {
    return (
      <Suspense fallback={<LoadingFallback message="Loading..." />}>
        <ResetPassword />
      </Suspense>
    );
  }

  // Show invite accept page without app shell (prevents remount flash
  // when auth state changes after registration)
  if (isInviteRoute) {
    return (
      <Suspense fallback={<LoadingFallback message="Loading..." />}>
        <Routes>
          <Route path="/invite/:token" element={<InviteAcceptWrapper />} />
        </Routes>
      </Suspense>
    );
  }

  // Show the account-activation page (admin-issued link) without app shell.
  if (isActivateRoute) {
    return (
      <Suspense fallback={<LoadingFallback message="Loading..." />}>
        <ActivateAccount />
      </Suspense>
    );
  }

  // Unauthenticated users can only access ESG, invites, activation, and
  // public auth pages. Redirect everything else to login (preserve
  // intended destination).
  if (
    !isAuthenticated &&
    !isLoading &&
    !isEsgRoute &&
    !isActivateRoute &&
    !isPublicAuthRoute &&
    !isNavMockupRoute
  ) {
    return <Navigate to={LOGIN_PATH} state={{ from: location.pathname }} replace />;
  }

  return (
    // Scoped inside AppContent because it needs the selected organisation, and
    // only wrapping the authenticated shell so the public/ESG routes above do
    // not poll an endpoint they have no session for.
    <ImportJobProvider organisationId={selectedOrganisationId || null}>
    <div className="App">
      <div className={`App-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="App-logo-container">
          <img src={logo} className="App-logo App-logo-full" alt="Econori logo" />
          <img
            src={`${import.meta.env.BASE_URL}econori-favicon.jpg`}
            className="App-logo-mark"
            alt="Econori"
          />
        </div>
        <Navbar onNavClick={() => setSidebarOpen(false)} />
      </div>
      <div
        className={`sidebar-backdrop${sidebarOpen ? ' visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      <main className="App-main">
        <TopBarSlotProvider>
        <UserHeader onMenuToggle={() => setSidebarOpen(prev => !prev)} />
        <div key={`org-${orgKey}`}>
          <SectionErrorBoundary sectionName={sectionName}>
            <Suspense fallback={<LoadingFallback message={`Loading ${sectionName}...`} />}>
              <Routes>
                {SECTION_ROUTES.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <ProtectedRoute
                        featureKey={route.featureKey}
                        internalOnly={route.internalOnly}
                      >
                        <route.component />
                      </ProtectedRoute>
                    }
                  />
                ))}
                {SUB_ROUTES.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <ProtectedRoute featureKey={route.featureKey}>
                        <route.component />
                      </ProtectedRoute>
                    }
                  />
                ))}
                <Route
                  path={ACCOUNT_PATH}
                  element={<AccountSettings />}
                />
                <Route path="/upgrade/:feature" element={<UpgradePage />} />
                <Route path="/upgrade-section/:section" element={<UpgradeSectionPage />} />
                <Route path="/measurement" element={<Navigate to="/settings/measurement" replace />} />
                <Route path="/sites" element={<Navigate to="/facilities" replace />} />
                <Route path="/vehicles" element={<Navigate to="/facilities" replace />} />
                {/* Legacy /reporting/v2* URLs now live at /reporting* */}
                <Route path="/reporting/v2" element={<ReportingV2Redirect />} />
                <Route path="/reporting/v2/*" element={<ReportingV2Redirect />} />
                {/* Retired reporting v1 — send the old internal /reporting/v1
                    URLs to v2 (v1 dashboard ids don't map across, so no prefix
                    rewrite). */}
                <Route path="/reporting/v1" element={<Navigate to="/reporting" replace />} />
                <Route path="/reporting/v1/*" element={<Navigate to="/reporting" replace />} />
                <Route path="/" element={<Navigate to={DEFAULT_PATH} replace />} />
                <Route path="*" element={<Navigate to={DEFAULT_PATH} replace />} />
              </Routes>
            </Suspense>
          </SectionErrorBoundary>
        </div>
        </TopBarSlotProvider>
      </main>

      {isAuthenticated && <SessionTimeoutWarning />}
      {isAuthenticated && <ForcePasswordUpgrade />}
    </div>
    </ImportJobProvider>
  );
}

function App(): React.ReactElement {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <PermissionProvider>
            <CurrencyDisplayProvider>
              <DashboardsProvider>
                <AppContent />
              </DashboardsProvider>
            </CurrencyDisplayProvider>
          </PermissionProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
